// Reinhard colour matching in CIELAB (SA B6.3). DOM-free, shared by the live
// shader (same constants, see render/stage.ts) and the still renderer.
//
// Each sender measures the Lab mean/std of its own person region and ships it
// with every pose message. Every receiver pulls each person part of the way
// toward the common target (the average of everybody), so two people filmed
// under different light end up in one tone — on every device identically.

import type { ColorStats } from './pose';

/** how far each person is pulled toward the common target (0 = off, 1 = full Reinhard) */
export const COLOR_STRENGTH = 0.6;
/** std ratio clamp so a flat-lit person is not blown up into noise */
export const STD_RATIO_MIN = 0.75;
export const STD_RATIO_MAX = 1.33;
/**
 * The a and b channels are only shifted (no std scaling) and by at most this much: the stats are
 * measured on the face (lighting cue), and a full chroma transfer would recolour
 * clothes or "correct" somebody's skin tone toward the other person's.
 */
export const CHROMA_MAX = 5;

const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
const gam = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);
const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
const finv = (t: number) => (t > 0.206893 ? t * t * t : (t - 16 / 116) / 7.787);
const XN = 0.95047, ZN = 1.08883;

/** sRGB (0..1) → CIELAB (L 0..100). */
export function rgbToLab(r: number, g: number, b: number): [number, number, number] {
  const R = lin(r), G = lin(g), B = lin(b);
  const x = (0.4124 * R + 0.3576 * G + 0.1805 * B) / XN;
  const y = 0.2126 * R + 0.7152 * G + 0.0722 * B;
  const z = (0.0193 * R + 0.1192 * G + 0.9505 * B) / ZN;
  const fx = f(x), fy = f(y), fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

/** CIELAB → sRGB (0..1, clamped). */
export function labToRgb(L: number, a: number, b: number): [number, number, number] {
  const fy = (L + 16) / 116, fx = fy + a / 500, fz = fy - b / 200;
  const x = finv(fx) * XN, y = finv(fy), z = finv(fz) * ZN;
  const R = 3.2406 * x - 1.5372 * y - 0.4986 * z;
  const G = -0.9689 * x + 1.8758 * y + 0.0415 * z;
  const B = 0.0557 * x - 0.204 * y + 1.057 * z;
  const c = (v: number) => Math.max(0, Math.min(1, gam(Math.max(0, v))));
  return [c(R), c(G), c(B)];
}

/**
 * Lab statistics of RGBA pixels weighted by a mask (0..1 per pixel, or the
 * pixel's own alpha when omitted). Returns null when the region is too small.
 */
export function labStats(px: ArrayLike<number>, weight?: ArrayLike<number>, step = 1): ColorStats | null {
  let n = 0;
  const s = [0, 0, 0], q = [0, 0, 0];
  for (let i = 0, p = 0; p < px.length; i += step, p = i * 4) {
    const w = weight ? weight[i] : px[p + 3] / 255;
    if (w < 0.5) continue;
    const lab = rgbToLab(px[p] / 255, px[p + 1] / 255, px[p + 2] / 255);
    for (let k = 0; k < 3; k++) { s[k] += lab[k]; q[k] += lab[k] * lab[k]; }
    n++;
  }
  if (n < 16) return null;
  const mean = s.map((v) => v / n) as [number, number, number];
  const std = q.map((v, k) => Math.sqrt(Math.max(1e-4, v / n - mean[k] * mean[k]))) as [number, number, number];
  return { mean, std };
}

/**
 * v0.9.3: with a real room as the scene, the people cut into it are pulled toward the scene
 * owner's exposure (Photoshop's "Match Color" toward the photo they are pasted into): the owner
 * is the photo, not one of the people to average.
 */
export const SCENE_STRENGTH = 0.6;
/**
 * v0.9.4: a person keeps their own skin tone — the colour of the LIGHT is matched by the server
 * (harmonize.ts, from the two rooms); here only brightness/contrast and a tiny residual shift
 */
export const SCENE_CHROMA_MAX = 2;

/** Target + strength for a shot: the scene owner's stats when there is a real-room scene, else the average. */
export function colorPlan(people: (ColorStats | null | undefined)[], scene: ColorStats | null | undefined): { target: ColorStats | null; strength: number; chroma: number } {
  if (scene) return { target: scene, strength: SCENE_STRENGTH, chroma: SCENE_CHROMA_MAX };
  return { target: colorTarget(people), strength: COLOR_STRENGTH, chroma: CHROMA_MAX };
}

/** Common target = average of everybody's stats (order-independent → deterministic). */
export function colorTarget(all: (ColorStats | null | undefined)[]): ColorStats | null {
  const xs = all.filter((s): s is ColorStats => !!s);
  if (xs.length < 2) return null; // nothing to match against
  const avg = (k: 'mean' | 'std', i: number) => xs.reduce((a, s) => a + s[k][i], 0) / xs.length;
  return { mean: [avg('mean', 0), avg('mean', 1), avg('mean', 2)], std: [avg('std', 0), avg('std', 1), avg('std', 2)] };
}

/** Per-channel affine Lab transform: lab' = lab * scale + offset. */
export interface ColorXform { scale: [number, number, number]; offset: [number, number, number] }

export const IDENTITY_XFORM: ColorXform = { scale: [1, 1, 1], offset: [0, 0, 0] };

/** Reinhard transfer src → dst, applied at `strength` (same maths as the shader). */
export function reinhardXform(src: ColorStats | null | undefined, dst: ColorStats | null | undefined, strength = COLOR_STRENGTH, chromaMax = CHROMA_MAX): ColorXform {
  if (!src || !dst) return IDENTITY_XFORM;
  const scale = [0, 0, 0] as [number, number, number];
  const offset = [0, 0, 0] as [number, number, number];
  // L: Reinhard mean + std  —  lab' = mix(L, (L - ms) * r + md, strength) = L * sc + off
  const r = Math.max(STD_RATIO_MIN, Math.min(STD_RATIO_MAX, dst.std[0] / Math.max(0.5, src.std[0])));
  scale[0] = 1 + (r - 1) * strength;
  offset[0] = (dst.mean[0] - src.mean[0] * r) * strength;
  // a, b: bounded white-balance shift only
  for (let k = 1; k < 3; k++) {
    scale[k] = 1;
    offset[k] = Math.max(-chromaMax, Math.min(chromaMax, (dst.mean[k] - src.mean[k]) * strength));
  }
  return { scale, offset };
}

/** Applies a Lab transform to RGBA pixels in place (alpha untouched, fully transparent pixels skipped). */
export function applyXform(px: Uint8ClampedArray, x: ColorXform) {
  if (x === IDENTITY_XFORM) return;
  for (let p = 0; p < px.length; p += 4) {
    if (px[p + 3] === 0) continue;
    const lab = rgbToLab(px[p] / 255, px[p + 1] / 255, px[p + 2] / 255);
    const rgb = labToRgb(lab[0] * x.scale[0] + x.offset[0], lab[1] * x.scale[1] + x.offset[1], lab[2] * x.scale[2] + x.offset[2]);
    px[p] = rgb[0] * 255; px[p + 1] = rgb[1] * 255; px[p + 2] = rgb[2] * 255;
  }
}

/** Temporal smoothing of the received stats (EMA), so tones do not pump frame to frame. */
export function smoothStats(prev: ColorStats | null, next: ColorStats | null, a = 0.1): ColorStats | null {
  if (!next) return prev;
  if (!prev) return next;
  const mix = (p: number, n: number) => p + (n - p) * a;
  return {
    mean: [mix(prev.mean[0], next.mean[0]), mix(prev.mean[1], next.mean[1]), mix(prev.mean[2], next.mean[2])],
    std: [mix(prev.std[0], next.std[0]), mix(prev.std[1], next.std[1]), mix(prev.std[2], next.std[2])],
  };
}
